/**
 * PDF Generation Utilities for AI Analysis
 * 
 * This module contains functions for generating PDF reports from various data sources
 * including questionnaire responses, HRV data, and HMA data.
 */

import jsPDF from 'jspdf';

export const addTextToPdf = (
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  fontSize: number = 12
): number => {
  pdf.setFontSize(fontSize);
  const lines = pdf.splitTextToSize(text, maxWidth);
  pdf.text(lines, x, y);
  return y + (lines.length * fontSize * 0.4);
};

export const checkNewPage = (
  pdf: jsPDF,
  yPosition: number,
  margin: number,
  requiredSpace: number = 40
): { needsNewPage: boolean; newYPosition: number } => {
  if (yPosition > pdf.internal.pageSize.height - requiredSpace) {
    pdf.addPage();
    return { needsNewPage: true, newYPosition: margin };
  }
  return { needsNewPage: false, newYPosition: yPosition };
};

export const extractValue = (field: any): string => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (field.value !== undefined) return field.value || '';
  return String(field);
};

export const formatQuestionId = (questionId: string): string => {
  return questionId.replace(/-/g, ' ').toUpperCase();
};

interface HRVItem {
  id: string;
  reportNumber?: string;
  dateOfAnalysis?: string;
  durationOfAnalysis?: number;
  attachmentUrl?: string;
  specialistReportUrl?: string;
  data?: any;
}

interface HMAItem {
  id: string;
  reportNumber?: string;
  dateOfAnalysis?: string;
  hairType?: string;
  attachmentUrl?: string;
  aluminum?: any;
  arsenic?: any;
  boron?: any;
  cadmium?: any;
  calcium?: any;
  chromium?: any;
  cobalt?: any;
  copper?: any;
  iron?: any;
  lead?: any;
  lithium?: any;
  magnesium?: any;
  manganese?: any;
  mercury?: any;
  molybdenum?: any;
  nickel?: any;
  potassium?: any;
  selenium?: any;
  zinc?: any;
}

interface ClientInfo {
  id?: string;
  firstName?: string;
  familyName?: string;
}

export const generateHRVAndHMAPdf = async (
  selectedHRVData: HRVItem[],
  selectedHMAData: HMAItem[],
  clientInfo: ClientInfo
): Promise<Blob> => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.width;
  const margin = 20;
  let yPosition = margin;

  const addText = (text: string, x: number, y: number, maxWidth: number, fontSize: number = 12) => {
    return addTextToPdf(pdf, text, x, y, maxWidth, fontSize);
  };

  const checkPage = (requiredSpace: number = 40) => {
    const result = checkNewPage(pdf, yPosition, margin, requiredSpace);
    yPosition = result.newYPosition;
    return result.needsNewPage;
  };

  // PDF Header
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  yPosition = addText('Analysis Data Report', margin, yPosition, pageWidth - 2 * margin, 20);
  yPosition += 10;

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  yPosition = addText(`Client: ${clientInfo?.firstName || ''} ${clientInfo?.familyName || ''}`, margin, yPosition, pageWidth - 2 * margin);
  yPosition = addText(`Client ID: ${clientInfo?.id || ''}`, margin, yPosition, pageWidth - 2 * margin);
  yPosition = addText(`Date Generated: ${new Date().toLocaleDateString()}`, margin, yPosition, pageWidth - 2 * margin);
  yPosition += 15;

  // HRV Data Section
  if (selectedHRVData.length > 0) {
    checkPage();
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    yPosition = addText('Heart Rate Variability (HRV) Data', margin, yPosition, pageWidth - 2 * margin, 16);
    yPosition += 10;

    selectedHRVData.forEach((hrv, index) => {
      checkPage(60);

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      yPosition = addText(`HRV Analysis #${index + 1}`, margin, yPosition, pageWidth - 2 * margin, 14);
      yPosition += 5;

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      yPosition = addText(`Report Number: ${hrv.reportNumber || 'N/A'}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition = addText(`Date of Analysis: ${hrv.dateOfAnalysis ? new Date(hrv.dateOfAnalysis).toLocaleDateString() : 'N/A'}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition = addText(`Duration: ${hrv.durationOfAnalysis || 'N/A'} days`, margin, yPosition, pageWidth - 2 * margin);
      
      if (hrv.attachmentUrl) {
        yPosition = addText(`Attachment URL: ${hrv.attachmentUrl}`, margin, yPosition, pageWidth - 2 * margin);
      }
      
      if (hrv.specialistReportUrl) {
        yPosition = addText(`Specialist Report URL: ${hrv.specialistReportUrl}`, margin, yPosition, pageWidth - 2 * margin);
      }

      // Add HRV data details if available
      if (hrv.data) {
        checkPage(40);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Measurement Data:', margin, yPosition, pageWidth - 2 * margin, 12);
        yPosition += 3;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        
        try {
          const hrvData = typeof hrv.data === 'string' ? JSON.parse(hrv.data) : hrv.data;
          const dataStr = JSON.stringify(hrvData, null, 2);
          const dataLines = dataStr.split('\n').slice(0, 30); // Limit to 30 lines
          
          dataLines.forEach(line => {
            checkPage(20);
            yPosition = addText(line, margin + 5, yPosition, pageWidth - 2 * margin - 5, 9);
          });
          
          if (dataStr.split('\n').length > 30) {
            yPosition = addText('... (data truncated)', margin + 5, yPosition, pageWidth - 2 * margin - 5, 9);
          }
        } catch (e) {
          yPosition = addText(`Raw data: ${String(hrv.data).substring(0, 200)}...`, margin + 5, yPosition, pageWidth - 2 * margin - 5, 9);
        }
      }

      yPosition += 10;
    });
  }

  // HMA Data Section
  if (selectedHMAData.length > 0) {
    checkPage();
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    yPosition = addText('Hair Mineral Analysis (HMA) Data', margin, yPosition, pageWidth - 2 * margin, 16);
    yPosition += 10;

    selectedHMAData.forEach((hma, index) => {
      checkPage(80);

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      yPosition = addText(`HMA Analysis #${index + 1}`, margin, yPosition, pageWidth - 2 * margin, 14);
      yPosition += 5;

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      yPosition = addText(`Report Number: ${hma.reportNumber || 'N/A'}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition = addText(`Date of Analysis: ${hma.dateOfAnalysis ? new Date(hma.dateOfAnalysis).toLocaleDateString() : 'N/A'}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition = addText(`Hair Type: ${hma.hairType || 'N/A'}`, margin, yPosition, pageWidth - 2 * margin);
      
      if (hma.attachmentUrl) {
        yPosition = addText(`Attachment URL: ${hma.attachmentUrl}`, margin, yPosition, pageWidth - 2 * margin);
      }
      
      yPosition += 5;

      // Mineral data
      checkPage(60);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      yPosition = addText('Mineral Levels:', margin, yPosition, pageWidth - 2 * margin, 12);
      yPosition += 3;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      const minerals = [
        { name: 'Aluminum', value: hma.aluminum },
        { name: 'Arsenic', value: hma.arsenic },
        { name: 'Boron', value: hma.boron },
        { name: 'Cadmium', value: hma.cadmium },
        { name: 'Calcium', value: hma.calcium },
        { name: 'Chromium', value: hma.chromium },
        { name: 'Cobalt', value: hma.cobalt },
        { name: 'Copper', value: hma.copper },
        { name: 'Iron', value: hma.iron },
        { name: 'Lead', value: hma.lead },
        { name: 'Lithium', value: hma.lithium },
        { name: 'Magnesium', value: hma.magnesium },
        { name: 'Manganese', value: hma.manganese },
        { name: 'Mercury', value: hma.mercury },
        { name: 'Molybdenum', value: hma.molybdenum },
        { name: 'Nickel', value: hma.nickel },
        { name: 'Potassium', value: hma.potassium },
        { name: 'Selenium', value: hma.selenium },
        { name: 'Zinc', value: hma.zinc },
      ];

      minerals.forEach(mineral => {
        if (mineral.value != null && mineral.value !== '') {
          checkPage(20);
          yPosition = addText(`  ${mineral.name}: ${mineral.value}`, margin + 5, yPosition, pageWidth - 2 * margin - 5, 10);
        }
      });

      yPosition += 10;
    });
  }

  return new Blob([pdf.output('blob')], { type: 'application/pdf' });
};
